import {
  CrmFileCard,
  CrmVenueImpact,
  CrmIssues,
  CrmResult,
  CrmSummary,
  CrmRowFilters,
  matchesCrmFilter,
  type CrmRowFilter,
} from './CrmWorkflowParts';
import { crmErrorCode, crmErrorText, CrmErrorDetails } from './CrmError';
import { useCrmAdapter } from './CrmWorkflowContext';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CRM_INCREMENT_VERSION,
  type MemberCrmIncrementDecision,
  type MemberCrmIncrementGroup,
  type MemberCrmIncrementPreview,
  type MemberCrmIncrementResult,
  type MemberCrmIncrementStatus,
  type MemberOpeningRouting,
  type MemberOpeningRoutingVenues,
} from '@pokerroom/types';
import { useAuthStore } from '../../store/auth.store';
import { api } from '../../lib/api';
import { ALERT, CHECK_LABEL, CHECKBOX, FIELD, OUTLINE, PRIMARY } from './importStyles';
import {
  beginIncrementAttempt,
  finishIncrementAttempt,
  incrementAttempts,
  incrementReceiptHash,
} from './TimedIncrementPanel.recovery';

export default function TimedIncrementPanel({ entryVenueId }: { entryVenueId: string }) {
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}:${s.sessionRevision}`);
  return <TimedIncrementForm key={`${identity}:${entryVenueId}`} entryVenueId={entryVenueId} />;
}
function TimedIncrementForm({ entryVenueId }: { entryVenueId: string }) {
  const { t } = useTranslation('member'),
    actor = useAuthStore((s) => s.user?.id ?? ''),
    session = useAuthStore((s) => s.sessionRevision);
  const [baseline, setBaseline] = useState<File | null>(null),
    [increment, setIncrement] = useState<File | null>(null),
    [chain, setChain] = useState<File[]>([]);
  const [rules, setRules] = useState<MemberOpeningRouting['rules']>([]),
    [assignments, setAssignments] = useState<Record<number, string>>({});
  const [preview, setPreview] = useState<MemberCrmIncrementPreview | null>(null),
    [decisions, setDecisions] = useState<Record<string, MemberCrmIncrementDecision>>({});
  const [fresh, setFresh] = useState(false),
    [armed, setArmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [pending, setPending] = useState(false),
    [checking, setChecking] = useState(false),
    [recovery, setRecovery] = useState('');
  const [result, setResult] = useState<MemberCrmIncrementResult | null>(null),
    [visible, setVisible] = useState(50);
  const [rowFilter, setRowFilter] = useState<CrmRowFilter>({ venue: '', blockedOnly: false });
  const sending = useRef(false);
  const query = useQuery({
    queryKey: ['opening-routing-venues', actor, session, entryVenueId],
    queryFn: () =>
      api.get<MemberOpeningRoutingVenues>('/members/import/timed-opening/routing-venues'),
    enabled: Boolean(actor && entryVenueId),
    retry: false,
    staleTime: 30000,
  });
  const context =
    query.data?.entryVenueId === entryVenueId &&
    typeof query.data.environment === 'string' &&
    query.data.environment.trim()
      ? query.data
      : null;
  const storageKey = context
    ? `crm-increment-attempts:${actor}:${entryVenueId}:${context.environment}`
    : '';
  const invalidate = (clear = false) => {
    setFresh(false);
    setArmed(false);
    setError('');
    setResult(null);
    if (clear) setDecisions({});
  };
  const [technicalError, setTechnicalError] = useState('');
  const fail = (e: unknown) => {
    const code = crmErrorCode(e);
    setTechnicalError(code);
    setError(crmErrorText(t, code));
    setFresh(false);
    setArmed(false);
  };
  const recover = async () => {
    if (!storageKey) return;
    const token = workflow.begin('lookup');
    if (!token) return;
    setChecking(true);
    try {
      const unresolved = incrementAttempts(sessionStorage, storageKey);
      const receipt = incrementReceiptHash(sessionStorage, storageKey);
      const hashes = unresolved.length ? unresolved : receipt ? [receipt] : [];
      setPending(unresolved.length > 0);
      if (!hashes.length) {
        setRecovery('');
        return;
      }
      let state = 'NOT_CONFIRMED';
      for (const planHash of hashes) {
        const value = await api.get<MemberCrmIncrementStatus>(
          '/members/import/timed-increment/status',
          { params: { planHash } },
        );
        if (value.state === 'CONFIRMED' && value.result) {
          finishIncrementAttempt(sessionStorage, storageKey, planHash, value.result);
          setResult(value.result);
          state = 'CONFIRMED';
        } else if (value.state === 'PENDING') state = 'PENDING';
        else if (value.state === 'CONFLICT' && state !== 'PENDING') state = 'CONFLICT';
        else if (value.state !== 'NOT_CONFIRMED') throw new Error('INCREMENT_RECEIPT_INVALID');
      }
      setPending(incrementAttempts(sessionStorage, storageKey).length > 0);
      setRecovery(
        incrementAttempts(sessionStorage, storageKey).length && state === 'CONFIRMED'
          ? 'NOT_CONFIRMED'
          : state,
      );
    } catch (e) {
      setPending(true);
      setRecovery('ERROR');
      fail(e);
    } finally {
      setChecking(false);
      workflow.end(token);
    }
  };
  useEffect(() => {
    if (storageKey) void recover();
  }, [storageKey]);
  const form = () => {
    if (!baseline || !increment) throw new Error('INCREMENT_FILES_INVALID');
    const grouped = new Map<string, number[]>();
    for (const [n, id] of Object.entries(assignments))
      if (id) grouped.set(id, [...(grouped.get(id) ?? []), Number(n)]);
    const routing: MemberOpeningRouting = {
      version: 'VENUE_DETAIL_OCT_2026_V1',
      rules,
      assignments: [...grouped].map(([venueId, rowNumbers]) => ({
        venueId,
        rowNumbers,
        fileSha256: preview?.file.sha256 ?? '',
      })),
    };
    const data = new FormData();
    data.append('baseline', baseline);
    data.append('increment', increment);
    for (const file of chain) data.append('chain', file);
    data.append(
      'payload',
      JSON.stringify({
        version: CRM_INCREMENT_VERSION,
        routing,
        decisions: Object.values(decisions),
        ...(preview ? { planHash: preview.planHash } : {}),
      }),
    );
    return data;
  };
  const workflow = useCrmAdapter(
    {
      purpose: 'INCREMENT',
      phase: pending
        ? 'UNKNOWN'
        : result
          ? 'RESULT'
          : armed
            ? 'CONFIRM'
            : preview
              ? 'PREVIEW'
              : 'FILES',
      dirty: !result && (!!baseline || !!increment || chain.length > 0 || !!preview),
      busy: busy || checking,
      unresolved: pending,
      canPreview:
        !busy &&
        !checking &&
        !pending &&
        !!context &&
        !!baseline &&
        !!increment &&
        chain.length <= 4,
      canConfirm:
        !!context && !query.isError && !!preview?.ready && fresh && armed && !pending && !busy,
    },
    {
      preview: () => {
        void requestPreview();
      },
      confirm: () => {
        void confirm();
      },
      back: () => {
        setPreview(null);
        setFresh(false);
        setArmed(false);
      },
    },
  );
  const requestPreview = async () => {
    if (busy || checking || pending) return;
    const workflowToken = workflow.begin();
    if (!workflowToken) return;
    setBusy(true);
    setArmed(false);
    setError('');
    try {
      const value = await api.post<MemberCrmIncrementPreview>(
        '/members/import/timed-increment/preview',
        form(),
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 45000 },
      );
      if (
        value.version !== CRM_INCREMENT_VERSION ||
        value.entryVenueId !== entryVenueId ||
        value.environment !== context?.environment ||
        !Array.isArray(value.rows) ||
        !Array.isArray(value.groups) ||
        !/^[a-f0-9]{64}$/.test(value.planHash)
      )
        throw new Error('INCREMENT_RECEIPT_INVALID');
      setPreview(value);
      setFresh(true);
      setVisible(50);
    } catch (e) {
      fail(e);
    } finally {
      workflow.end(workflowToken);
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!context || query.isError) return;
    if (!preview?.ready || !fresh || !armed || !storageKey || pending || busy || sending.current)
      return;
    const planHash = preview.planHash;
    const workflowToken = workflow.begin();
    if (!workflowToken) return;
    setBusy(true);
    sending.current = true;
    setError('');
    let recorded = false;
    try {
      beginIncrementAttempt(sessionStorage, storageKey, planHash);
      recorded = true;
      setPending(true);
      const value = await api.post<MemberCrmIncrementResult>(
        '/members/import/timed-increment/confirm',
        form(),
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 70000 },
      );
      finishIncrementAttempt(sessionStorage, storageKey, planHash, value);
      setResult(value);
      setPending(incrementAttempts(sessionStorage, storageKey).length > 0);
      setFresh(false);
      setRecovery('CONFIRMED');
    } catch (e) {
      // Only this fresh attempt's known rollback may release it. Never erase an earlier UNKNOWN.
      const data = (
        e as {
          response?: {
            status?: number;
            data?: { code?: string; errorCode?: string; message?: string };
          };
        }
      )?.response;
      const code = data?.data?.errorCode ?? data?.data?.code ?? data?.data?.message;
      if (
        recorded &&
        [400, 403, 409].includes(data?.status ?? 0) &&
        typeof code === 'string' &&
        /^(INCREMENT_(?:PAYLOAD_INVALID|FILES_INVALID|EXPORT_INVALID|DECISION_INVALID|PLAN_HASH_REQUIRED|PLAN_STALE|HOLD|ROUTING_HOLD|SAME_CONTENT|NOTHING_TO_APPLY|PERIOD_EXPIRED|ALREADY_CONFIRMED|FILE_ALREADY_COMMITTED|DUPLICATE_OCCURRENCE|MEMBER_NUMBER_CONFLICT)|IMPORT_ACCESS_DENIED|FEATURE_NOT_AVAILABLE)$/.test(
          code,
        )
      ) {
        try {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify(
              incrementAttempts(sessionStorage, storageKey).filter((h) => h !== planHash),
            ),
          );
          setPending(incrementAttempts(sessionStorage, storageKey).length > 0);
        } catch {
          setPending(true);
        }
      } else {
        setPending(true);
        setRecovery('ERROR');
      }
      fail(e);
    } finally {
      sending.current = false;
      workflow.end(workflowToken);
      setBusy(false);
      setArmed(false);
    }
  };
  const changeDecision = (
    g: MemberCrmIncrementGroup,
    patch: Partial<MemberCrmIncrementDecision>,
  ) => {
    const d = decisions[g.groupKey] ?? {
      groupKey: g.groupKey,
      period: '2026-10' as const,
      state: 'NO_OVERLAP' as const,
      evidenceMode: 'EXTERNAL' as const,
      explanation: '',
      evidenceReference: '',
      evidenceDigest: '',
      sourceCutoff: preview!.file.exportedAt,
      attested: false,
      checkedLedgerIds: [],
      allocations: [],
    };
    setDecisions({ ...decisions, [g.groupKey]: { ...d, ...patch } });
    invalidate();
  };
  const hold = (code: string) => crmErrorText(t, code);
  const locked = busy || checking || pending;
  return (
    <section className="space-y-5" aria-label={t('increment.title')}>
      <h2 className="text-lg font-semibold">{t('increment.title')}</h2>
      <p className="text-sm text-base-content">{t('increment.scope')}</p>
      {!context && (
        <div role="alert" className={ALERT}>
          {query.isError ? t('opening.venuesFailed') : t('increment.loading')}{' '}
          {query.isError && (
            <button type="button" className={OUTLINE} onClick={() => void query.refetch()}>
              {t('opening.retryVenues')}
            </button>
          )}
        </div>
      )}
      {error && (
        <div role="alert" className={ALERT}>
          {error}
          <CrmErrorDetails code={technicalError} />
        </div>
      )}
      {result && (
        <CrmResult receipt={result.batchId}>
          {t(result.alreadyConfirmed ? 'increment.already' : 'increment.done')}{' '}
        </CrmResult>
      )}
      {(pending || recovery) && (
        <div role="status" className={ALERT}>
          <p>
            {recovery === 'CONFIRMED' ? t('increment.confirmedReceipt') : t('increment.recovery')}
          </p>
          <button
            type="button"
            className={OUTLINE}
            disabled={busy || checking}
            onClick={() => void recover()}
          >
            {checking ? t('increment.loading') : t('opening.checkStatus')}
          </button>
        </div>
      )}
      {preview && !result && (
        <CrmSummary
          rows={preview.file.rows}
          issues={
            preview.totals.heldRows + preview.batchHolds.length + preview.routingIssues.length
          }
          facts={[
            {
              label: t('crmWorkspace.membersCount'),
              value: new Set(preview.groups.flatMap((r) => (r.memberId ? [r.memberId] : []))).size,
            },
            { label: t('crmWorkspace.venuesCount'), value: preview.venues?.length ?? 0 },
            { label: t('crmWorkspace.sourcePeriod'), value: '2026-10' },
          ]}
          ready={preview.ready && fresh}
        />
      )}
      <fieldset disabled={locked || !context} className="grid gap-4 md:grid-cols-3">
        <CrmFileCard>
          <label className="block space-y-1 text-sm">
            {t('increment.baseline')}
            <input
              className={`${FIELD} w-full`}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setBaseline(e.target.files?.[0] ?? null);
                setPreview(null);
                setAssignments({});
                invalidate(true);
              }}
            />
          </label>
        </CrmFileCard>
        <CrmFileCard>
          <label className="block space-y-1 text-sm">
            {t('increment.chain')}
            <input
              className={`${FIELD} w-full`}
              type="file"
              accept=".xlsx"
              multiple
              onChange={(e) => {
                setChain(Array.from(e.target.files ?? []));
                invalidate(true);
              }}
            />
          </label>
        </CrmFileCard>
        <CrmFileCard>
          <label className="block space-y-1 text-sm">
            {t('increment.file')}
            <input
              className={`${FIELD} w-full`}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setIncrement(e.target.files?.[0] ?? null);
                setPreview(null);
                setAssignments({});
                invalidate(true);
              }}
            />
          </label>
        </CrmFileCard>
      </fieldset>
      <fieldset disabled={locked || !context} className="space-y-2">
        <legend className="font-semibold">{t('opening.routingTitle')}</legend>
        <p className="text-sm">{t('opening.routingHelp')}</p>
        {rules.map((rule, i) => (
          <div key={i} className="flex flex-wrap gap-2">
            <input
              aria-label={`${t('opening.keyword')} ${i + 1}`}
              className={FIELD}
              maxLength={100}
              value={rule.keyword}
              onChange={(e) => {
                setRules(rules.map((r, n) => (n === i ? { ...r, keyword: e.target.value } : r)));
                setAssignments({});
                invalidate(true);
              }}
            />
            <select
              aria-label={`${t('opening.destination')} ${i + 1}`}
              className={FIELD}
              value={rule.venueId}
              onChange={(e) => {
                setRules(rules.map((r, n) => (n === i ? { ...r, venueId: e.target.value } : r)));
                setAssignments({});
                invalidate(true);
              }}
            >
              {context?.venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={OUTLINE}
              onClick={() => {
                setRules(rules.filter((_, n) => n !== i));
                setAssignments({});
                invalidate(true);
              }}
            >
              {t('opening.removeRule')}
            </button>
          </div>
        ))}
        <button
          type="button"
          className={OUTLINE}
          disabled={rules.length >= 100}
          onClick={() => {
            setRules([...rules, { keyword: '', venueId: entryVenueId }]);
            invalidate(true);
          }}
        >
          {t('opening.addRule')}
        </button>
      </fieldset>
      {!workflow.embedded && (
        <button
          type="button"
          className={PRIMARY}
          disabled={locked || !context || !baseline || !increment || chain.length > 4}
          onClick={() => void requestPreview()}
        >
          {busy ? t('increment.loading') : t(preview ? 'opening.repreview' : 'opening.preview')}
        </button>
      )}
      {preview && (
        <>
          {!!preview.batchHolds.length && (
            <ul role="alert" className={ALERT}>
              {preview.batchHolds.map((c) => (
                <li key={c}>{hold(c)}</li>
              ))}
            </ul>
          )}
          {!!preview.routingIssues.length && (
            <fieldset disabled={locked} className="space-y-2">
              <legend>{t('opening.unresolvedRouting')}</legend>
              {preview.routingIssues.slice(0, visible).map((r) => (
                <label key={r.rowNumber} className="flex flex-wrap gap-2">
                  {t('opening.sourceRows')}: {r.rowNumber} {r.remark || t('opening.blankRemark')}
                  <select
                    className={FIELD}
                    aria-label={`${t('opening.destination')} ${r.rowNumber}`}
                    value={assignments[r.rowNumber] ?? ''}
                    onChange={(e) => {
                      setAssignments({ ...assignments, [r.rowNumber]: e.target.value });
                      invalidate(true);
                    }}
                  >
                    <option value="">{t('opening.chooseVenue')}</option>
                    {context?.venues.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </fieldset>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-live="polite">
            {(['newRows', 'recoveredRows', 'heldRows', 'credit'] as const).map((k) => (
              <div key={k} className="border border-base-300 p-3">
                <p className="text-sm">{t(`increment.${k}`)}</p>
                <strong className="tabular-nums text-xl">{preview.totals[k]}</strong>
              </div>
            ))}
          </div>
          <CrmVenueImpact
            columns={[
              t('opening.destination'),
              t('increment.newRows'),
              t('increment.recoveredRows'),
              t('increment.credit'),
            ]}
            rows={preview.venues.map((v) => ({
              key: v.venueId,
              cells: [v.name, v.newRows, v.recoveredRows, v.credit],
            }))}
          />
          <CrmIssues codes={[...preview.batchHolds, ...preview.groups.flatMap((g) => g.holds)]} />
          <CrmRowFilters
            value={rowFilter}
            onChange={setRowFilter}
            venues={context?.venues ?? []}
            total={preview.groups.length}
            matched={preview.groups.filter((row) => matchesCrmFilter(row, rowFilter)).length}
            limit={visible}
            onMore={() => setVisible((n) => n + 50)}
          />
          {preview.groups
            .filter((row) => matchesCrmFilter(row, rowFilter))
            .slice(0, visible)
            .map((g) => (
              <details
                key={g.groupKey}
                className="border border-base-300 p-3"
                open={g.holds.length > 0}
              >
                <summary className="min-h-11 cursor-pointer break-words">
                  {context?.venues.find((v) => v.id === g.venueId)?.name ?? t('opening.unresolved')}{' '}
                  · {t('opening.sourceRows')}: {g.sourceRows.slice(0, 6).join(', ')}
                  {g.sourceRows.length > 6 ? '…' : ''} · {t('increment.credit')}: {g.credit}
                </summary>
                <p>
                  {t('increment.balance', { before: g.beforeBalance, after: g.resultingBalance })}
                </p>
                <ul>
                  {g.holds.map((h) => (
                    <li key={h}>{hold(h)}</li>
                  ))}
                </ul>
                {g.ledger.length > 0 && (
                  <fieldset disabled={locked} className="space-y-3">
                    <legend>{t('opening.evidenceHelp')}</legend>
                    <label className="block">
                      {t('opening.decision')}
                      <select
                        className={`${FIELD} block w-full`}
                        value={decisions[g.groupKey]?.state ?? ''}
                        onChange={(e) =>
                          changeDecision(g, {
                            state: e.target.value as 'NO_OVERLAP' | 'PROVEN_OVERLAP',
                          })
                        }
                      >
                        <option value="" disabled>
                          {t('increment.chooseEvidence')}
                        </option>
                        <option value="NO_OVERLAP">{t('opening.states.NO_OVERLAP')}</option>
                        <option value="PROVEN_OVERLAP">{t('opening.states.PROVEN_OVERLAP')}</option>
                      </select>
                    </label>
                    {(['explanation', 'evidenceReference', 'evidenceDigest'] as const).map(
                      (field) => (
                        <label key={field} className="block">
                          {t(`opening.${field}`)}
                          <input
                            className={`${FIELD} block w-full`}
                            maxLength={
                              field === 'explanation'
                                ? 1000
                                : field === 'evidenceReference'
                                  ? 255
                                  : 64
                            }
                            value={decisions[g.groupKey]?.[field] ?? ''}
                            onChange={(e) => changeDecision(g, { [field]: e.target.value })}
                          />
                        </label>
                      ),
                    )}
                    <p className="text-sm">
                      {t('opening.cutoff')}: {preview.file.exportedAt}
                    </p>
                    {g.ledger.map((l) => (
                      <div key={l.id} className="flex flex-wrap items-center gap-2">
                        <label className={CHECK_LABEL}>
                          <input
                            className={CHECKBOX}
                            type="checkbox"
                            checked={
                              decisions[g.groupKey]?.checkedLedgerIds.includes(l.id) ?? false
                            }
                            onChange={(e) => {
                              const ids = decisions[g.groupKey]?.checkedLedgerIds ?? [];
                              changeDecision(g, {
                                checkedLedgerIds: e.target.checked
                                  ? [...ids, l.id]
                                  : ids.filter((id) => id !== l.id),
                              });
                            }}
                          />
                          {l.createdAt} · {l.delta}
                        </label>
                        <label>
                          {t('opening.allocation')}
                          <input
                            className={`${FIELD} w-28`}
                            inputMode="numeric"
                            value={
                              decisions[g.groupKey]?.allocations.find((a) => a.ledgerId === l.id)
                                ?.amount ?? ''
                            }
                            onChange={(e) => {
                              const rest = (decisions[g.groupKey]?.allocations ?? []).filter(
                                (a) => a.ledgerId !== l.id,
                              );
                              changeDecision(g, {
                                allocations: e.target.value
                                  ? [...rest, { ledgerId: l.id, amount: e.target.value }]
                                  : rest,
                              });
                            }}
                          />
                        </label>
                      </div>
                    ))}
                    <label className={CHECK_LABEL}>
                      <input
                        type="checkbox"
                        className={CHECKBOX}
                        checked={decisions[g.groupKey]?.attested ?? false}
                        onChange={(e) => changeDecision(g, { attested: e.target.checked })}
                      />
                      {t('opening.attestation')}
                    </label>
                  </fieldset>
                )}
              </details>
            ))}
          {preview.routingIssues.length > visible && (
            <button type="button" className={OUTLINE} onClick={() => setVisible(visible + 50)}>
              {t('opening.showMore')}
            </button>
          )}
          <label className={CHECK_LABEL}>
            <input
              type="checkbox"
              className={CHECKBOX}
              checked={armed}
              disabled={!fresh || !preview.ready || locked}
              onChange={(e) => setArmed(e.target.checked)}
            />
            {t('increment.confirmNotice')}
          </label>
          {!workflow.embedded && (
            <button
              type="button"
              className={PRIMARY}
              disabled={!fresh || !preview.ready || !armed || locked}
              onClick={() => void confirm()}
            >
              {busy ? t('increment.loading') : t('increment.confirm')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
